import { Box } from "@mui/material";
import LinearProgress from "components/LinearProgress";
import { useEffect } from "react";
import Markdown from "react-markdown";
import { useLocation } from "react-router-dom";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import { useFetchDoc } from "utils/query";
import { DocLoadError } from "./components/DocLoadError";
import { DocNotFound } from "./components/DocNotFound";
import { DocsPager } from "./components/DocsPager";
import { MarkdownContent } from "./components/MarkdownContent";
import { MarkdownImage } from "./components/MarkdownImage";
import { MarkdownLink } from "./components/MarkdownLink";
import { adjacentDocs, findDoc } from "./docsManifest";

interface DocsPageProps {
  slug: string;
}

export const DocsPage = ({ slug }: DocsPageProps) => {
  const doc = findDoc(slug);
  const { hash } = useLocation();
  // An unknown slug never reaches the API: the hook stays disabled on an empty slug.
  const { data, error, status, refetch } = useFetchDoc(doc ? slug : "");

  useEffect(() => {
    if (data?.content && hash) {
      document
        .getElementById(decodeURIComponent(hash.slice(1)))
        ?.scrollIntoView();
    }
  }, [data, hash]);

  if (!doc) {
    return <DocNotFound />;
  }
  if (status === "error") {
    if ((error as Response | null)?.status === 404) {
      return <DocNotFound />;
    }
    return <DocLoadError onRetry={() => refetch()} />;
  }
  // "idle" (waiting on login state before the query may run) and "loading" both land here.
  if (status !== "success") {
    return <LinearProgress />;
  }
  // A 200 with an empty body parses to null: an empty .md file, treated like a missing one.
  if (!data?.content) {
    return <DocNotFound />;
  }

  const { prev, next } = adjacentDocs(slug);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <MarkdownContent>
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSlug]}
          components={{
            a: MarkdownLink,
            img: (props) => <MarkdownImage docSlug={slug} {...props} />,
          }}
        >
          {data.content}
        </Markdown>
      </MarkdownContent>
      <DocsPager prev={prev} next={next} />
    </Box>
  );
};
